// HomePage.tsx
import React, { useState, useEffect, useRef } from "react";
import { motion, useInView, MotionConfig } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  Scale,
  MessageSquare,
  FileText,
  DollarSign,
  Calendar,
  Shield,
  PenTool,
  BarChart3,
  Users,
  Phone,
  Mail,
  Smartphone,
  Landmark,
  BookOpen,
  Briefcase,
  Gavel,
  Heart,
  Building2,
  Globe,
  HardHat,
  Leaf,
  Brain,
  Zap,
  Clock,
  BadgeCheck,
  ChevronRight,
  FolderOpen,
  Star,
} from "lucide-react";
import FooterSection from "./FooterSection";
import LoginModal from "./LoginModal";
import SignUpModal from "./SignUpModal";
import Logo from "../assets/KavoaLogo.png";
import PracticeAreaRequestModal from "./ui/PracticeAreaRequestModal";

interface HomepageProps {
  onSuccessfulLogin: (data: { token: string; userId: string }) => void;
}

// ── Animated counter ────────────────────────────────────────────────
const AnimatedNumber: React.FC<{ value: number; suffix?: string }> = ({ value, suffix = "" }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let frame = 0;
    const totalFrames = 40;
    const step = value / totalFrames;
    const timer = setInterval(() => {
      frame++;
      setDisplay(Math.min(Math.round(step * frame), value));
      if (frame >= totalFrames) clearInterval(timer);
    }, 30);
    return () => clearInterval(timer);
  }, [isInView, value]);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
};

// ── Mobile detection ─────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
};

// ── Section wrapper for consistent viewport animations ──────────────
const Section: React.FC<{
  children: React.ReactNode;
  className?: string;
  id?: string;
}> = ({ children, className = "", id }) => {
  return (
    <motion.section
      id={id}
      className={className}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5 }}
    >
      {children}
    </motion.section>
  );
};

// ── Data ────────────────────────────────────────────────────────────

const painPoints = [
  {
    icon: Zap,
    problem: "5+ disconnected tools",
    detail:
      "Case management here, billing there, DocuSign over there, a separate phone system, and QuickBooks for trust accounting. Nothing talks to anything.",
  },
  {
    icon: Clock,
    problem: "Hours lost to admin work",
    detail:
      "Switching between systems, re-entering data, and manually tracking deadlines. Every extra click is time you can't bill.",
  },
  {
    icon: Shield,
    problem: "Compliance anxiety",
    detail:
      "Trust accounting rules are unforgiving. One mistake can mean an audit, a grievance, or worse. Spreadsheets aren't good enough.",
  },
];

const coreFeatures = [
  {
    icon: Users,
    title: "Client & Matter CRM",
    desc: "Complete client profiles, matter tracking, notes, and relationship history. Every detail in one place.",
  },
  {
    icon: DollarSign,
    title: "Time Tracking & Billing",
    desc: "Track time, generate professional invoices, accept online payments, and set up payment plans.",
  },
  {
    icon: Landmark,
    title: "Trust Accounting",
    desc: "Law society compliant trust ledgers, three-way reconciliation, disbursements, and full audit trails.",
  },
  {
    icon: MessageSquare,
    title: "SMS, Email & Voice",
    desc: "Text, email, and call clients from one unified inbox. Every conversation linked to the right matter.",
  },
  {
    icon: PenTool,
    title: "E-Signatures",
    desc: "Send documents for signature and track status. Signed copies stored automatically in the matter file.",
  },
  {
    icon: FolderOpen,
    title: "Document Management",
    desc: "Organized folders, version control, templates, and full-text search across every file in your firm.",
  },
  {
    icon: Calendar,
    title: "Calendar & Deadlines",
    desc: "Syncs with Google Calendar. Court dates, deadlines, and reminders -all visible from your dashboard.",
  },
  {
    icon: BarChart3,
    title: "Leads & Intake",
    desc: "Kanban pipeline for new leads. Intake forms, follow-up tracking, and one-click conversion to clients.",
  },
  {
    icon: FileText,
    title: "Reports & Analytics",
    desc: "Revenue by matter, billable hours, trust balances, aged receivables, and firm performance at a glance.",
  },
];

const practiceAreas = [
  { icon: Gavel, name: "Criminal Defense" },
  { icon: Globe, name: "Immigration" },
  { icon: Heart, name: "Family Law" },
  { icon: Scale, name: "Personal Injury" },
  { icon: Building2, name: "Corporate" },
  { icon: Briefcase, name: "Employment" },
  { icon: BookOpen, name: "Estates & Trusts" },
  { icon: HardHat, name: "Construction" },
  { icon: Leaf, name: "Environmental" },
  { icon: Scale, name: "Litigation" },
  { icon: Brain, name: "Intellectual Property" },
  { icon: Landmark, name: "Tax Law" },
  { icon: Building2, name: "Real Estate" },
  { icon: Shield, name: "Insurance Defense" },
  { icon: Scale, name: "Class Action" },
  { icon: Gavel, name: "Administrative Law" },
  { icon: Star, name: "Entertainment" },
  { icon: Zap, name: "Technology" },
];

const replacesTools = [
  { name: "Practice Management", examples: "Clio, MyCase, PracticePanther" },
  { name: "Billing & Invoicing", examples: "FreshBooks, TimeSolv" },
  { name: "Trust Accounting", examples: "QuickBooks, LeanLaw" },
  { name: "E-Signatures", examples: "DocuSign, HelloSign" },
  { name: "Client Messaging", examples: "Lawmatics, Kenect" },
  { name: "Calendar & Tasks", examples: "Calendly, Todoist" },
];

// ── Component ───────────────────────────────────────────────────────

const HomePage: React.FC<HomepageProps> = ({ onSuccessfulLogin }) => {
  const isMobile = useIsMobile();
  const [loginOpen, setLoginOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);

  const scrollToFeatures = () => {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen w-full bg-white overflow-x-hidden">
      {/* Modals */}
      <LoginModal isOpen={loginOpen} onOpenChange={setLoginOpen} onSuccessfulLogin={onSuccessfulLogin} />
      <SignUpModal isOpen={signupOpen} onOpenChange={setSignupOpen} onSuccessfulLogin={onSuccessfulLogin} />
      <PracticeAreaRequestModal isOpen={requestModalOpen} onClose={() => setRequestModalOpen(false)} />

      {/* ───────────────────── Navigation ───────────────────── */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-1">
          <div className="flex items-center justify-between">
            <motion.div
              className="flex items-center gap-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <img src={Logo} alt="Kavoa" className="h-20 rounded-lg object-contain" />
            </motion.div>

            <motion.div
              className="flex items-center gap-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <button
                onClick={scrollToFeatures}
                className="px-5 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors hidden md:block"
              >
                Features
              </button>
              <a
                href="/pricing"
                className="px-5 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
              >
                Pricing
              </a>
              <button
                onClick={() => setLoginOpen(true)}
                className="px-5 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => setSignupOpen(true)}
                className="px-6 py-2.5 text-sm font-semibold text-white rounded-xl transition-all shadow-md hover:shadow-lg"
                style={{ backgroundColor: "#0F5132" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#146C43";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#0F5132";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                Start Free Trial
              </button>
            </motion.div>
          </div>
        </div>
      </nav>

      {/* ───────────────────── Hero ───────────────────── */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50/40" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 40%, rgba(15, 81, 50, 0.04) 0%, transparent 50%), radial-gradient(circle at 75% 70%, rgba(25, 135, 84, 0.03) 0%, transparent 50%)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 lg:pt-24 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left -copy */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-emerald-100 shadow-sm mb-8"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <BadgeCheck className="h-4 w-4" style={{ color: "#198754" }} />
                <span className="text-sm font-semibold" style={{ color: "#0F5132" }}>
                  One Platform. Every Tool You Need.
                </span>
              </motion.div>

              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold mb-8 leading-[1.08] tracking-tight text-slate-900">
                Stop juggling tools.
                <br />
                <span style={{ color: "#0F5132" }}>
                  Start practicing{" "}
                  <span className="relative inline-block">
                    <span className="relative z-10">law.</span>
                    <motion.span
                      className="absolute bottom-1 lg:bottom-2 left-0 h-3 lg:h-4 bg-gradient-to-r from-emerald-200/70 to-emerald-300/70 -z-0 rounded-sm"
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{ delay: 0.8, duration: 0.6 }}
                    />
                  </span>
                </span>
              </h1>

              <p className="text-lg lg:text-xl text-slate-600 mb-10 leading-relaxed max-w-xl">
                Kavoa replaces your practice management, billing, trust accounting,
                document management, e-signatures, and client texting and calling
                with one modern platform that actually works together.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <button
                  onClick={() => setSignupOpen(true)}
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 text-base font-semibold text-white rounded-xl transition-all shadow-lg hover:shadow-2xl"
                  style={{ backgroundColor: "#0F5132" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#146C43";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#0F5132";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  Start Your Free Trial
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <a
                  href="/pricing"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold bg-white border-2 border-slate-200 rounded-xl transition-all hover:border-slate-300 hover:shadow-md text-slate-700"
                >
                  View Pricing
                </a>
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" style={{ color: "#198754" }} />
                  <span>14-day free trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" style={{ color: "#198754" }} />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" style={{ color: "#198754" }} />
                  <span>Set up in minutes</span>
                </div>
              </div>
            </motion.div>

            {/* Right -feature showcase */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative hidden lg:block"
            >
              <div className="absolute -top-10 -right-10 w-72 h-72 bg-gradient-to-br from-emerald-200 to-emerald-400 rounded-full blur-3xl opacity-15" />
              <div className="absolute -bottom-10 -left-10 w-72 h-72 bg-gradient-to-br from-slate-200 to-slate-400 rounded-full blur-3xl opacity-15" />

              <div className="relative bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden">
                {/* Window bar */}
                <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50/60">
                  <div className="h-3 w-3 rounded-full bg-red-300" />
                  <div className="h-3 w-3 rounded-full bg-yellow-300" />
                  <div className="h-3 w-3 rounded-full bg-green-300" />
                  <span className="ml-3 text-xs text-slate-400 font-medium">Kavoa Dashboard</span>
                </div>

                <div className="p-6 space-y-4">
                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: "Active Matters", value: "24", trend: "+3 this week" },
                      { label: "Hours This Week", value: "31.5", trend: "On track" },
                      { label: "Outstanding", value: "$18,420", trend: "4 invoices" },
                    ].map((stat, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 + i * 0.1, duration: 0.4 }}
                        className="bg-slate-50 rounded-xl p-3 border border-slate-100"
                      >
                        <p className="text-xs text-slate-500 mb-1">{stat.label}</p>
                        <p className="text-lg font-bold text-slate-800">{stat.value}</p>
                        <p className="text-xs text-emerald-600 font-medium">{stat.trend}</p>
                      </motion.div>
                    ))}
                  </div>

                  {/* Urgent tasks */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-slate-700">Today's Priorities</h4>
                      <span className="px-2.5 py-1 bg-red-50 text-red-600 text-xs font-bold rounded-full border border-red-100">
                        2 overdue
                      </span>
                    </div>
                    {[
                      { title: "File motion for summary judgment", matter: "Smith v. Johnson Corp", urgency: "red", time: "2 days overdue" },
                      { title: "Submit I-485 adjustment application", matter: "Martinez Immigration", urgency: "orange", time: "Due today" },
                      { title: "Review closing documents", matter: "123 Oak St. Purchase", urgency: "slate", time: "Due tomorrow" },
                    ].map((task, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.8 + i * 0.12, duration: 0.4 }}
                        className="flex items-center gap-3 py-2.5 border-b border-slate-50 last:border-b-0"
                      >
                        <div
                          className={`h-2 w-2 rounded-full flex-shrink-0 ${
                            task.urgency === "red" ? "bg-red-500" : task.urgency === "orange" ? "bg-orange-400" : "bg-slate-300"
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{task.title}</p>
                          <p className="text-xs text-slate-400">{task.matter}</p>
                        </div>
                        <span
                          className={`text-xs font-semibold flex-shrink-0 ${
                            task.urgency === "red" ? "text-red-500" : task.urgency === "orange" ? "text-orange-500" : "text-slate-400"
                          }`}
                        >
                          {task.time}
                        </span>
                      </motion.div>
                    ))}
                  </div>

                  {/* Recent messages */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">Recent Messages</h4>
                    {[
                      { from: "Maria Santos", channel: "SMS", preview: "Thank you for the update on my case...", icon: Smartphone },
                      { from: "James Chen", channel: "Email", preview: "Attached are the signed documents...", icon: Mail },
                    ].map((msg, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.1 + i * 0.12, duration: 0.4 }}
                        className="flex items-center gap-3 py-2.5 border-b border-slate-50 last:border-b-0"
                      >
                        <div className="h-8 w-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                          <msg.icon className="h-3.5 w-3.5" style={{ color: "#0F5132" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-slate-800">{msg.from}</p>
                            <span className="text-xs text-slate-400">{msg.channel}</span>
                          </div>
                          <p className="text-xs text-slate-400 truncate">{msg.preview}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───────────────────── Stats bar ───────────────────── */}
      <Section className="py-12 border-y border-slate-100 bg-slate-50/50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: 18, suffix: "+", label: "Practice Areas Supported" },
              { number: 6, suffix: "", label: "Tools Replaced in One Platform" },
              { number: 14, suffix: "-Day", label: "Free Trial, No Card Required" },
              { number: 100, suffix: "%", label: "Trust Accounting Compliance" },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
              >
                <div className="text-3xl lg:text-4xl font-bold mb-1" style={{ color: "#0F5132" }}>
                  <AnimatedNumber value={stat.number} suffix={stat.suffix} />
                </div>
                <p className="text-sm text-slate-500 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ───────────────────── The Problem ───────────────────── */}
      <Section className="py-24 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: "#198754" }}>
              The Problem
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-slate-900">
              Your firm deserves better than duct-taped software
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
              Most law firms run on a patchwork of disconnected tools that don't share data,
              waste time, and create compliance risk.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {painPoints.map((p, i) => {
              const Icon = p.icon;
              const cardContent = (
                <>
                  <div className="h-12 w-12 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center mb-5 group-hover:bg-red-100 transition-colors">
                    <Icon className="h-6 w-6 text-red-500" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{p.problem}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{p.detail}</p>
                </>
              );
              return isMobile ? (
                <div key={i} className="relative bg-white rounded-2xl p-8 border border-slate-200 hover:border-red-200 hover:shadow-lg transition-all duration-300 group">
                  {cardContent}
                </div>
              ) : (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12, duration: 0.6 }}
                  className="relative bg-white rounded-2xl p-8 border border-slate-200 hover:border-red-200 hover:shadow-lg transition-all duration-300 group"
                >
                  {cardContent}
                </motion.div>
              );
            })}
          </div>

          {/* Transition line */}
          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-emerald-50 border border-emerald-100">
              <CheckCircle className="h-5 w-5" style={{ color: "#198754" }} />
              <span className="text-sm font-semibold" style={{ color: "#0F5132" }}>
                Kavoa solves all three in one platform
              </span>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* ───────────────────── Core Features ───────────────────── */}
      <Section id="features" className="py-24 lg:py-28 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: "#198754" }}>
              The Platform
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-slate-900">
              Everything your firm needs. Nothing it doesn't.
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
              Nine integrated modules that share data, automate workflows,
              and keep your entire practice in sync.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreFeatures.map((f, i) => {
              const Icon = f.icon;
              const cardContent = (
                <>
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/40 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative">
                    <div
                      className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-5 group-hover:scale-110 transition-transform"
                      style={{ backgroundColor: "#E8F5E9" }}
                    >
                      <Icon className="h-6 w-6" style={{ color: "#0F5132" }} />
                    </div>
                    <h3 className="text-base font-bold mb-2 text-slate-800">{f.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                  </div>
                </>
              );
              return isMobile ? (
                <div key={i} className="group relative bg-white rounded-2xl p-7 border border-slate-100 hover:border-emerald-200 hover:shadow-xl transition-all duration-300">
                  {cardContent}
                </div>
              ) : (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.07, duration: 0.5 }}
                  className="group relative bg-white rounded-2xl p-7 border border-slate-100 hover:border-emerald-200 hover:shadow-xl transition-all duration-300"
                >
                  {cardContent}
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ───────────────────── Communications Spotlight ───────────────────── */}
      <Section className="py-24 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left -visual */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-slate-50 to-emerald-50/30 rounded-3xl p-8 border border-slate-100">
                {/* Channel cards */}
                <div className="space-y-4">
                  {[
                    {
                      icon: Smartphone,
                      channel: "SMS",
                      from: "Maria Santos",
                      preview: "Hi, just wanted to confirm our meeting tomorrow at 2pm?",
                      time: "2 min ago",
                      color: "#0F5132",
                      bg: "#E8F5E9",
                    },
                    {
                      icon: Mail,
                      channel: "Email",
                      from: "James Chen",
                      preview: "Attached are the signed settlement documents for your review.",
                      time: "15 min ago",
                      color: "#1e40af",
                      bg: "#dbeafe",
                    },
                    {
                      icon: Phone,
                      channel: "Voice",
                      from: "Sarah Johnson",
                      preview: "Incoming call -linked to Matter #2847",
                      time: "1 hour ago",
                      color: "#7c3aed",
                      bg: "#ede9fe",
                    },
                  ].map((msg, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + i * 0.15, duration: 0.5 }}
                      className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: msg.bg }}
                        >
                          <msg.icon className="h-5 w-5" style={{ color: msg.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-slate-800">{msg.from}</p>
                              <span
                                className="text-xs font-medium px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: msg.bg, color: msg.color }}
                              >
                                {msg.channel}
                              </span>
                            </div>
                            <span className="text-xs text-slate-400">{msg.time}</span>
                          </div>
                          <p className="text-sm text-slate-500 truncate">{msg.preview}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right -copy */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: "#198754" }}>
                Client Communications
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6 text-slate-900 leading-tight">
                Every conversation. One inbox. Zero context switching.
              </h2>
              <p className="text-base text-slate-500 mb-8 leading-relaxed">
                Send and receive SMS messages, emails, and phone calls directly from Kavoa.
                Every message is automatically linked to the right client and matter -so you
                never have to search for context again.
              </p>
              <ul className="space-y-4">
                {[
                  "SMS and email from local business numbers",
                  "Full conversation history per client and matter",
                  "Gmail and Google Calendar sync built-in",
                  "Compose rich HTML emails with attachments",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: "#198754" }} />
                    <span className="text-sm text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* ───────────────────── Trust Accounting Spotlight ───────────────────── */}
      <Section className="py-24 lg:py-28 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left -copy */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: "#198754" }}>
                Trust Accounting
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6 text-slate-900 leading-tight">
                Trust compliance without the spreadsheet anxiety
              </h2>
              <p className="text-base text-slate-500 mb-8 leading-relaxed">
                Kavoa's trust accounting module is purpose-built for law firms. Client ledgers,
                three-way reconciliation, disbursement tracking, and full audit trails -all
                integrated with your billing so nothing falls through the cracks.
              </p>
              <ul className="space-y-4">
                {[
                  "Per-client trust sub-ledgers with real-time balances",
                  "Three-way reconciliation (bank, book, client)",
                  "Automated disbursement and receipt tracking",
                  "Complete audit trail for every transaction",
                  "Chart of accounts customizable to your jurisdiction",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Shield className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: "#198754" }} />
                    <span className="text-sm text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Right -visual */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-xl">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                  <h4 className="text-lg font-bold text-slate-800">Trust Account Overview</h4>
                  <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-100">
                    Reconciled
                  </span>
                </div>
                <div className="space-y-4">
                  {[
                    { client: "Smith v. Johnson Corp", balance: "$45,000.00", status: "Active" },
                    { client: "Martinez Immigration", balance: "$12,500.00", status: "Active" },
                    { client: "Oak Street Purchase", balance: "$8,200.00", status: "Pending disbursement" },
                    { client: "Chen Estate", balance: "$67,350.00", status: "Active" },
                  ].map((row, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.1, duration: 0.4 }}
                      className="flex items-center justify-between py-3 border-b border-slate-50 last:border-b-0"
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-800">{row.client}</p>
                        <p className="text-xs text-slate-400">{row.status}</p>
                      </div>
                      <p className="text-sm font-bold text-slate-800">{row.balance}</p>
                    </motion.div>
                  ))}
                </div>
                <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-500">Total Trust Balance</span>
                  <span className="text-xl font-bold" style={{ color: "#0F5132" }}>$133,050.00</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* ───────────────────── Practice Areas ───────────────────── */}
      <Section className="py-24 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: "#198754" }}>
              Built for How You Practice
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-slate-900">
              Dedicated workflows for 18+ practice areas
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
              Not a generic project manager with a legal skin. Each practice area has custom fields,
              stage tracking, and workflows that match how you actually work.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {practiceAreas.map((area, i) => {
              const Icon = area.icon;
              const cardContent = (
                <>
                  <div
                    className="h-10 w-10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: "#E8F5E9" }}
                  >
                    <Icon className="h-5 w-5" style={{ color: "#0F5132" }} />
                  </div>
                  <span className="text-xs font-semibold text-slate-600 text-center leading-tight">
                    {area.name}
                  </span>
                </>
              );
              return isMobile ? (
                <div key={i} className="group flex flex-col items-center gap-3 p-5 bg-slate-50 rounded-2xl border border-slate-100 hover:bg-white hover:border-emerald-200 hover:shadow-md transition-all duration-300 cursor-default">
                  {cardContent}
                </div>
              ) : (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.03, duration: 0.4 }}
                  className="group flex flex-col items-center gap-3 p-5 bg-slate-50 rounded-2xl border border-slate-100 hover:bg-white hover:border-emerald-200 hover:shadow-md transition-all duration-300 cursor-default"
                >
                  {cardContent}
                </motion.div>
              );
            })}
          </div>

          {/* Custom practice area CTA */}
          <motion.div
            className="mt-10 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
              <p className="text-sm font-semibold text-slate-800 mb-1">
                Don't see your practice area?
              </p>
              <p className="text-sm text-slate-500">
                Tell us what you need and we'll build a custom workflow for your firm, free of charge.
                We're always adding new practice areas.
              </p>
              <button
                onClick={() => setRequestModalOpen(true)}
                className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 text-sm font-semibold rounded-xl border-2 transition-all hover:shadow-md"
                style={{ borderColor: "#0F5132", color: "#0F5132" }}
              >
                <Mail className="h-4 w-4" />
                Let us know
              </button>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* ───────────────────── Replaces Section ───────────────────── */}
      <Section className="py-24 lg:py-28 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: "#198754" }}>
              Consolidate Your Stack
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-slate-900">
              One subscription. Six tools replaced.
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              Stop paying for tools that don't talk to each other.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {replacesTools.map((tool, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="flex items-center gap-4 bg-white rounded-xl p-5 border border-slate-200 hover:border-emerald-200 hover:shadow-md transition-all"
              >
                <div className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#E8F5E9" }}>
                  <CheckCircle className="h-5 w-5" style={{ color: "#0F5132" }} />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800">{tool.name}</p>
                  <p className="text-xs text-slate-400">Replaces {tool.examples}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ───────────────────── Testimonial ───────────────────── */}
      <Section className="py-24 lg:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            className="relative bg-gradient-to-br from-slate-50 to-emerald-50/30 rounded-3xl p-10 lg:p-14 border border-slate-100"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            {/* Large quote mark */}
            <div
              className="absolute top-6 left-8 text-7xl font-serif leading-none select-none opacity-10"
              style={{ color: "#0F5132" }}
            >
              &ldquo;
            </div>

            <div className="relative">
              <blockquote className="text-lg lg:text-xl text-slate-700 leading-relaxed mb-8 font-medium">
                I used to spend hours every week just getting invoices out the door. With Kavoa, I generate
                a bill in minutes and my clients can pay online instantly. Setting up payment plans is one click.
                My clients can upload documents directly when I send them reminders, and the trust accounting
                finally gives me confidence that everything is reconciled. Being able to text clients directly
                from a case file has been a game changer - they actually respond. I spend less time on admin
                and more time actually practicing law.
              </blockquote>

              <div className="flex items-center gap-4">
                {/* Janssen Law logo text styled to match their branding */}
                <div className="flex-shrink-0 h-12 w-12 rounded-full bg-slate-900 flex items-center justify-center">
                  <span className="text-white font-bold text-lg" style={{ letterSpacing: "-0.02em" }}>J</span>
                </div>
                <div>
                  <p className="text-base font-bold text-slate-900">Charlotte Janssen</p>
                  <p className="text-sm text-slate-500">
                    Principal -{" "}
                    <span style={{ fontWeight: 700, letterSpacing: "-0.02em", color: "#000" }}>
                      Janssen Law
                    </span>{" "}
                    Professional Corporation, Toronto
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Corporate, Immigration, Real Estate &amp; Estates
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* ───────────────────── Final CTA ───────────────────── */}
      <section className="py-24 lg:py-28 relative overflow-hidden" style={{ backgroundColor: "#0F5132" }}>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.04) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.03) 0%, transparent 50%)",
          }}
        />

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-white leading-tight">
              Ready to run your firm on one platform?
            </h2>
            <p className="text-lg text-emerald-100/80 mb-10 max-w-2xl mx-auto leading-relaxed">
              14-day free trial with full access to every feature. No credit card.
              No commitment. Set up in under five minutes.
            </p>

            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <button
                onClick={() => setSignupOpen(true)}
                className="group inline-flex items-center justify-center gap-3 px-10 py-4 text-base font-bold bg-white rounded-xl transition-all shadow-2xl hover:shadow-3xl hover:scale-[1.02]"
                style={{ color: "#0F5132" }}
              >
                Start Your Free Trial
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="/pricing"
                className="inline-flex items-center justify-center gap-2 px-10 py-4 text-base font-semibold text-white rounded-xl border-2 border-white/20 hover:border-white/40 transition-all"
              >
                View Pricing
              </a>
            </div>

            <p className="text-sm text-emerald-200/60 mt-8">
              Join law firms already simplifying their practice with Kavoa
            </p>
          </motion.div>
        </div>
      </section>

      {/* ───────────────────── Footer ───────────────────── */}
      <FooterSection />
    </div>
  );
};

export default HomePage;
